import React, { useEffect } from 'react'
import { GetStaticProps } from 'next'
import { StaticImageData } from 'next/image'
import { map } from 'ramda'

import Accounts, { Account } from '@ecm/data/Account'
import { MarketingErrorResponse } from '@ecm/data/Api'
import { Cart } from '@ecm/data/Cart'
import { Coupon, isCoupon } from '@ecm/data/Coupon'
import { getSha256Hash } from '@ecm/data/Hash'
import { Lead } from '@ecm/data/Lead'
import { Partner, PartnerSlug } from '@ecm/data/Partner'
import { runClientEff, runEff } from '@ecm/effect'
import PartnersPage from '@ecm/ui/component/PartnersPage'
import Color from '@ecm/ui/element/Color'
import DefaultLayout from '@ecm/ui/layout/Default'
import { LEGAL_ZOOM_VIRTUAL_MAIL_PAGE_WITH_STRIPE_PARAMS } from '@ecm/utils/legalZoomRedirects'

import stripeAtlasLogo from '/public/images/logo/stripe-atlas-partner.svg'
import stripeAtlasSvbLogo from '/public/images/logo/stripe-atlas-svb-partner.svg'

const partnerLogoMap: Record<string, StaticImageData> = {
  'stripe-atlas': stripeAtlasLogo,
  'stripe-atlas-svb': stripeAtlasSvbLogo,
}

export type PartnerLandingPageProps = {
  className?: string
  partner: Partner
}

export const PartnerLandingPage = ({ partner }: PartnerLandingPageProps) => {
  const hasDiscount = partner.discountPercent && partner.discountPercent > 0
  const title = hasDiscount ? (
    <>
      <span style={{ color: Color.lucky.normal }}>{partner.discountPercent}% off</span> for{' '}
      {partner.name} customers
    </>
  ) : null

  useEffect(() => {
    runClientEff(async eff => {
      if (partner.promoCode) {
        const coupon: Coupon | MarketingErrorResponse = await eff.getCoupon(partner.promoCode)

        if (isCoupon(coupon)) {
          eff.updateStorageItem<Cart>('cart', { coupon: coupon })
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSuccess = (lead: Lead) => {
    return () => {
      runClientEff(eff => {
        try {
          eff.setStorageItem<Account>(
            'account',
            Accounts.account({
              email: lead.email,
              firstName: lead.firstName || '',
              lastName: lead.lastName || '',
            })
          )

          eff.trackGa({
            cp_customer_email: getSha256Hash(lead.email),
            event: 'lpLeadFormSuccess',
          })

          window.location.href = LEGAL_ZOOM_VIRTUAL_MAIL_PAGE_WITH_STRIPE_PARAMS
        } catch (e) {
          console.log(e)
        }
      })
    }
  }
  const onFailure = (message: string) => {
    return () => {
      console.log(message)
    }
  }
  return (
    <DefaultLayout
      hideLinks
      hideMobileLinks
      noRobots
      hideFinalCta
      hideFooter
      logo={partnerLogoMap[partner.slug]}
      seoTitle={`${partner.name} Partnership | Earth Class Mail`}
      seoDescription={`We’ve teamed up with ${partner.name} to offer you a digital mail solution that any business owner should have.`}
    >
      <PartnersPage
        hasDiscount={hasDiscount}
        onSuccess={onSuccess}
        onFailure={onFailure}
        partner={partner}
        title={title}
      />
    </DefaultLayout>
  )
}

export const getStaticProps: GetStaticProps = async ctx => {
  const slug = ctx.params?.slug as PartnerSlug
  const isPreview = false // Static content mode - no preview
  const partner: Partner = await runEff(async eff => eff.getPartner(slug, isPreview))
  return { props: { partner } }
}

export async function getStaticPaths() {
  const isPreview = false // Static content mode - no preview
  const partnerPages: Partner[] = await runEff(eff => eff.getPartners(isPreview))

  const paths = map(p => ({ params: p }), partnerPages)

  return {
    fallback: false,
    paths,
  }
}

export default PartnerLandingPage
